// =====================================================================================
// INVRT Radar - web console - SearchField (canonical mirror, Story PS1)
//
// Mirror of the INVRT Design System's SearchField (components/Primitives/SearchField.jsx +
// SearchField.d.ts). Its own component rather than an Input with an icon, because search
// has behaviour an input does not: a shortcut hint, a clear button that appears only when
// there is something to clear, and a busy state while results resolve. It is a search
// landmark, so it carries role="search". This retires the raw `Input type="search"` the
// operator console filtered with (invest gap C1).
// =====================================================================================

import { useState, type FormEvent } from "react";
import { Loader2, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SearchFieldProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  /** Hint shown while empty: "⌘K", "/". Only render one that actually works. */
  shortcut?: string;
  size?: "sm" | "md" | "lg";
  /** Swaps the clear button for a spinner while results resolve. */
  loading?: boolean;
  label?: string;
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<SearchFieldProps["size"]>, string> = {
  sm: "h-8 text-sm",
  md: "h-[calc(var(--control-h-lg)+2px)] text-sm",
  lg: "h-[calc(var(--control-h-lg)+2px)] text-base",
};

export function SearchField({
  value,
  defaultValue = "",
  onChange,
  onSubmit,
  placeholder = "Search",
  shortcut,
  size = "md",
  loading,
  label = "Search",
  className,
}: SearchFieldProps) {
  const [inner, setInner] = useState(defaultValue);
  const current = value ?? inner;
  const set = (next: string) => {
    if (value === undefined) setInner(next);
    onChange?.(next);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit?.(current);
  };

  return (
    <form
      role="search"
      data-size={size}
      onSubmit={submit}
      className={cn(
        "flex w-full items-center gap-2 rounded-lg border border-field-border bg-surface px-3 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-1",
        SIZE_CLASSES[size],
        className,
      )}
    >
      <Search className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
      <input
        type="search"
        value={current}
        placeholder={placeholder}
        aria-label={label}
        onChange={(event) => set(event.target.value)}
        className="h-full w-full bg-transparent text-ink placeholder:text-ink-muted focus-visible:outline-none"
      />
      {loading ? (
        <span role="status" aria-label="Searching" className="flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-ink-muted" aria-hidden="true" />
        </span>
      ) : current ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => set("")}
          className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : shortcut ? (
        <kbd className="rounded border border-line bg-surface-muted px-1.5 py-0.5 font-mono text-xs text-ink-muted">
          {shortcut}
        </kbd>
      ) : null}
    </form>
  );
}
